/**
 * MSPs and their Clients (Story 48, ADR-0033, task 205): the organization
 * tree. `GET /orgs` is `read`; every change is `manage_org`, held at the
 * caller's home node and below (`Me.permissions.manage_org`). A node outside
 * the caller's subtree is refused exactly like one that does not exist.
 * Clients are created with `createTenant`, not here.
 */
import { request } from './client.js';
import type {
  CreateOrgBody,
  CreateOrgResult,
  ListOrgsResult,
  OrgAuditPage,
  OrgNode,
  OrgSettingBody,
  OrgSettingKey,
  OrgSettingView,
  UpdateOrgBody,
} from './types.js';

function one(orgId: string): string {
  return `/orgs/${encodeURIComponent(orgId)}`;
}

export function listOrgs(idToken: string): Promise<ListOrgsResult> {
  return request<ListOrgsResult>('GET', '/orgs', idToken);
}

/** 409 `email_exists` for the first Admin, `not_resellable`; 400 `bad_parent`. */
export function createOrg(body: CreateOrgBody, idToken: string): Promise<CreateOrgResult> {
  return request<CreateOrgResult>('POST', '/orgs', idToken, { body });
}

/** 409 `cycle`, `has_active_children`, `last_admin`, `not_resellable` or `conflict`. */
export function updateOrg(orgId: string, body: UpdateOrgBody, idToken: string): Promise<OrgNode> {
  return request<OrgNode>('PATCH', one(orgId), idToken, { body });
}

/**
 * Replace an MSP's resale, from a strict ancestor. 409 `not_resellable`
 * beyond the parent's set; `in_use`, naming the Clients and MSPs that still
 * hold a removed service.
 */
export function putResellable(orgId: string, services: string[], idToken: string): Promise<OrgNode> {
  return request<OrgNode>('PUT', `${one(orgId)}/resellable`, idToken, { body: { services } });
}

/** Newest first; pass `next_cursor` back for the next page. */
export function listOrgAudit(
  orgId: string,
  idToken: string,
  cursor?: string | null,
): Promise<OrgAuditPage> {
  return request<OrgAuditPage>('GET', `${one(orgId)}/audit`, idToken, { query: { cursor } });
}

// ---------- settings through the tree (task 206) ----------

function setting(orgId: string, key: OrgSettingKey): string {
  return `${one(orgId)}/settings/${key}`;
}

/** Readable with a Client below the node in scope, or `manage_org` at it. */
export function getOrgSetting(
  orgId: string,
  key: OrgSettingKey,
  idToken: string,
): Promise<OrgSettingView> {
  return request<OrgSettingView>('GET', setting(orgId, key), idToken);
}

/** This node's own value. 409 `conflict` when `expected_version` is stale. */
export function putOrgSetting(
  orgId: string,
  key: OrgSettingKey,
  body: OrgSettingBody,
  idToken: string,
): Promise<OrgSettingView> {
  return request<OrgSettingView>('PUT', setting(orgId, key), idToken, { body });
}

/** Revert to inherited. 400 `root_setting` at the root: its tier is the system tier. */
export function revertOrgSetting(
  orgId: string,
  key: OrgSettingKey,
  idToken: string,
): Promise<OrgSettingView> {
  return request<OrgSettingView>('DELETE', setting(orgId, key), idToken);
}
